/**
 * Returns the current UK tax year as a string (e.g., "2025-26").
 *
 * UK tax years run from April 6 to April 5. For example:
 * - April 6, 2025 to April 5, 2026 is tax year "2025-26"
 * - April 5, 2025 is still in tax year "2024-25"
 *
 * Uses Europe/London timezone to correctly handle the April 5/6 boundary.
 *
 * @returns Tax year string in format "YYYY-YY"
 */
export function getCurrentTaxYearString(): TaxYearString {
  // Get current date in UK timezone (handles BST/GMT automatically)
  const ukDateStr = new Date().toLocaleDateString("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Parse UK date string (DD/MM/YYYY format)
  const [dayStr, monthStr, yearStr] = ukDateStr.split("/");
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);

  // Tax year starts April 6 (month 4, day 6)
  // If before April 6, we're in the previous tax year
  const startYear = month < 4 || (month === 4 && day < 6) ? year - 1 : year;
  const endYearShort = (startYear + 1) % 100;

  return `${startYear}-${endYearShort.toString().padStart(2, "0")}` as TaxYearString;
}

/**
 * All supported tax years for the calculator.
 * Use this for dropdowns and validation.
 *
 * SYNC: Must match calculator/src/tax_year.rs::TaxYear::all()
 * and backend/core/tax_year.py::SUPPORTED_TAX_YEARS
 *
 * When adding a new tax year, update:
 * 1. calculator/src/tax_year.rs (TaxYear enum + TAX_RATES registry)
 * 2. backend/core/tax_year.py (SUPPORTED_TAX_YEARS)
 * 3. frontend/src/utils/taxYear.ts (this file)
 * 4. backend/core/constants.py (get_employer_ni_constants)
 */
export const SUPPORTED_TAX_YEARS = ["2024-25", "2025-26", "2026-27"] as const;

/**
 * Type for tax year strings (e.g., "2024-25", "2025-26").
 */
export type TaxYearString = (typeof SUPPORTED_TAX_YEARS)[number];

/**
 * Gets the date boundaries for a given UK tax year (6 April to 5 April).
 *
 * @param taxYear - Tax year string (e.g., "2025-26")
 * @returns Object with taxYearStart and taxYearEnd dates
 *
 * @example
 * getTaxYearBounds("2025-26") // { taxYearStart: Date(2025-04-06), taxYearEnd: Date(2026-04-05) }
 */
export function getTaxYearBounds(taxYear: TaxYearString): { taxYearStart: Date; taxYearEnd: Date } {
  const startYear = parseInt(taxYear.split('-')[0], 10);
  return {
    taxYearStart: new Date(startYear, 3, 6),
    taxYearEnd: new Date(startYear + 1, 3, 5, 23, 59, 59, 999),
  };
}

/**
 * Gets the boundaries of the current UK tax year (6 April to 5 April).
 *
 * @returns Object with taxYearStart and taxYearEnd dates
 *
 * @example
 * // If today is 2025-06-01
 * getCurrentTaxYear() // { taxYearStart: Date(2025-04-06), taxYearEnd: Date(2026-04-05) }
 *
 * // If today is 2025-03-01
 * getCurrentTaxYear() // { taxYearStart: Date(2024-04-06), taxYearEnd: Date(2025-04-05) }
 */
export function getCurrentTaxYear(): { taxYearStart: Date; taxYearEnd: Date } {
  return getTaxYearBounds(getCurrentTaxYearString());
}

/**
 * Checks if a date string falls within the current UK tax year (6 April to 5 April).
 *
 * @param dateStr - ISO date string (YYYY-MM-DD)
 * @returns true if the date is in the current UK tax year, false otherwise
 *
 * @example
 * // If today is 2025-06-01
 * isInCurrentTaxYear('2025-05-01') // true (2025/26 tax year)
 * isInCurrentTaxYear('2025-04-05') // false (2024/25 tax year)
 * isInCurrentTaxYear('2025-04-06') // true (2025/26 tax year)
 */
export function isInCurrentTaxYear(dateStr: string): boolean {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;

  const { taxYearStart, taxYearEnd } = getCurrentTaxYear();
  return d >= taxYearStart && d <= taxYearEnd;
}
